import React, { forwardRef } from "react"
import { RankingData } from "../types"
import { truncateEns, convertDatabaseFormatToEmoji } from "../utils/dataProcessor"
import "ethereum-identity-kit/css"
import { ProfileStats, Avatar } from "ethereum-identity-kit"
import StatBars from "./StatBars"

interface RankCardProps {
  data: RankingData
}

function getRankStyle(rank: number) {
  if (rank <= 10)
    return { title: "Challenger", glow: "shadow-[0_0_25px_rgba(255,140,0,0.75)]", accent: "#FF8C00", accentTo: "#FF4500", bg: "#1a1405" }
  if (rank <= 100)
    return { title: "Grandmaster", glow: "shadow-[0_0_25px_rgba(120,0,255,0.7)]", accent: "#C084FC", accentTo: "#7C3AED", bg: "#140a22" }
  if (rank <= 300)
    return { title: "Master", glow: "shadow-[0_0_25px_rgba(99,102,241,0.6)]", accent: "#6366F1", accentTo: "#4338CA", bg: "#07131f" }
  if (rank <= 800)
    return { title: "Diamond", glow: "shadow-[0_0_25px_rgba(34,211,238,0.5)]", accent: "#22D3EE", accentTo: "#0891B2", bg: "#06190f" }
  if (rank <= 2000)
    return { title: "Gold", glow: "shadow-[0_0_25px_rgba(250,204,21,0.5)]", accent: "#FACC15", accentTo: "#CA8A04", bg: "#1a1206" }
  if (rank <= 5000)
    return { title: "Silver", glow: "shadow-[0_0_25px_rgba(220,220,220,0.4)]", accent: "#E5E7EB", accentTo: "#9CA3AF", bg: "#121216" }
  if (rank <= 10000)
    return { title: "Bronze", glow: "shadow-[0_0_25px_rgba(50,50,50,0.6)]", accent: "#D69B6A", accentTo: "#92400E", bg: "#1a1009" }
  return { title: "Iron", glow: "shadow-[0_0_25px_rgba(100,116,139,0.5)]", accent: "#64748B", accentTo: "#334155", bg: "#0e0e10" }
}

function shortAddress(addr: string): string {
  return addr.length > 16 ? `${addr.slice(0, 6)}...${addr.slice(-6)}` : addr
}

const RankCard = forwardRef<HTMLDivElement, RankCardProps>(({ data }, ref) => {
  const rankStyle = getRankStyle(data.Rank)
  const rankColorClass = getRankColorClass(data.Rank)
  const fullName = truncateEns(convertDatabaseFormatToEmoji(data.ensName)) || data.address
  const displayName =
    fullName.startsWith("0x") && fullName.length > 16 ? shortAddress(fullName) : fullName
  const gradient = `linear-gradient(135deg, ${rankStyle.accent}, ${rankStyle.accentTo})`

  return (
    <div className="cursor-pointer active:scale-95 transition-all w-[350px] md:w-[420px] mx-auto">
      <div
        ref={ref}
        className={`
          relative w-full mx-auto rounded-2xl overflow-hidden
          text-white shadow-2xl border-[2px] backdrop-blur-xl
          ${rankStyle.glow}
          scale-90
        `}
        style={{
          background: `color-mix(in srgb, ${rankStyle.accent} 20%, #0b0b0d)`,
          borderColor: rankStyle.accent,
        }}
      >
        <div className="flex items-center justify-between px-5 pt-5">
          <span
            className="text-xl font-extrabold tracking-wide bg-clip-text text-transparent"
            style={{ backgroundImage: gradient, WebkitBackgroundClip: "text", backgroundClip: "text" }}
          >
            {rankStyle.title}
          </span>
          <span className="flex items-baseline gap-1">
            <span className="text-xs text-white/50">NO.</span>
            <span className="text-2xl font-extrabold leading-none" style={{ color: rankStyle.accent }}>
              {data.Rank}
            </span>
          </span>
        </div>

        <div className="flex flex-col items-center pt-6 pb-5">
          <div className="relative w-36 h-36 rounded-full p-[3px]" style={{ background: gradient }}>
            <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-black/40">
              <Avatar address={data.address} name={data.ensName} />
            </div>
          </div>
          <div className={`mt-4 text-2xl font-semibold text-center ${rankColorClass}`}>
            {displayName}
          </div>
          <div className="mt-2 [&_.profile-stats-item-label]:text-white/50 [&_.profile-stats-item-label]:text-xs [&_.profile-stats-item-label]:font-semibold">
            <ProfileStats addressOrName={data.address} />
          </div>
        </div>

        <div className="px-5 py-5 text-center border-t border-white/5">
          <div className="text-xs text-white/50 tracking-[0.2em] uppercase">EFPower</div>
          <div
            className="text-4xl font-extrabold bg-clip-text text-transparent"
            style={{ backgroundImage: gradient, WebkitBackgroundClip: "text", backgroundClip: "text" }}
          >
            {data.EFPower.toLocaleString()}
          </div>
        </div>

        <div className="px-5 pb-6">
          <StatBars
            base={data.basePower}
            more={data.morePower}
            buff={data.buffPower}
            accent={rankStyle.accent}
            accentTo={rankStyle.accentTo}
          />
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-white/5 text-sm text-white/45">
          <span className="text-base">#{data.listId}</span>
          <span className="flex items-center gap-1.5">
            ENS
            <span
              className={`w-2 h-2 rounded-full ${
                data.ensName && data.ensName.includes(".")
                  ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.7)]"
                  : "bg-red-400"
              }`}
            />
          </span>
        </div>
      </div>
    </div>
  )
})

RankCard.displayName = "RankCard"

function getRankColorClass(rank: number): string {
  if (rank <= 10) return "text-amber-400"
  if (rank <= 100) return "text-violet-400"
  if (rank <= 300) return "text-sky-400"
  if (rank <= 800) return "text-emerald-400"
  if (rank <= 2000) return "text-amber-200"
  if (rank <= 5000) return "text-zinc-200"
  if (rank <= 10000) return "text-zinc-400"
  return "text-gray-400"
}

export default RankCard
